import React, { useState, useEffect } from 'react';
import PostCard from '../components/PostCard';
import Sidebar from '../components/Sidebar';
import EntitySelector from '../components/EntitySelector';
import { Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import { useOrganization } from '../hooks/useOrganization';
import { useCurrentUser } from '../hooks/useCurrentUser';

interface Like {
  user_id: string;
  created_at: string;
}

interface Comment {
  id: string;
  user_id: string;
  content: string;
  created_at: string;
}

interface EntityFromAPI {
  id: string;
  name: string;
  profile_picture?: string | null;
  profile_picture_url?: string | null;
}

interface Post {
  id: string;
  organization_id: string;
  entity_id: string;
  category_name: string;
  content: string;
  likes: Like[];
  comments: Comment[];
  archived: boolean;
  deleted: boolean;
  created_at: string;
  updated_at: string;
  user_id: string;
  entity: EntityFromAPI;
}

interface CreatePostForm {
  entity_id: string;
  category_name: string;
  content: string;
}


const Feed: React.FC = () => {
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [isCreatePostModalOpen, setIsCreatePostModalOpen] = useState(false);
  const [entities, setEntities] = useState<EntityFromAPI[]>([]);
  const [posts, setPosts] = useState<Post[]>([]);
  const [isLoadingPosts, setIsLoadingPosts] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);
  const [createPostForm, setCreatePostForm] = useState<CreatePostForm>({
    entity_id: '',
    category_name: '',
    content: ''
  });
  const [postContent, setPostContent] = useState('');
  const [whatsappBroadcast, setWhatsappBroadcast] = useState(false);
  const [isLoadingEntities, setIsLoadingEntities] = useState(false);
  const { isOfficialOrganization, organization } = useOrganization();
  const { user: currentUser } = useCurrentUser();

  // Convert EntityFromAPI to format expected by EntitySelector
  const convertEntitiesForSelector = (entities: EntityFromAPI[]) => {
    const converted = entities.map(entity => ({
      id: entity.id,
      name: entity.name,
      profile_picture: entity.profile_picture || entity.profile_picture_url || null,
      profile_picture_url: entity.profile_picture_url || entity.profile_picture || null
    }));
    console.log('Original entities:', entities);
    console.log('Converted entities:', converted);
    return converted;
  };

  useEffect(() => {
    fetchPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPosts = async (page: number = 1) => {
    setIsLoadingPosts(true);
    try {
      const token = getTokenFromCookies();
      if (!token) {
        setIsLoadingPosts(false);
        return;
      }

      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '10',
        order_by: 'created_at',
        order_direction: 'desc'
      });

      const response = await fetch(`${API_BASE_URL}/posts?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPosts(data.data?.posts || []);
        setTotalPages(data.data?.total_pages || 1);
        setTotalPosts(data.data?.total || 0);
        setCurrentPage(page);
      } else {
        console.error('Erro ao buscar posts:', response.status);
      }
    } catch (error) {
      console.error('Erro ao buscar posts:', error);
    } finally {
      setIsLoadingPosts(false);
    }
  };

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3030';

  const getTokenFromCookies = () => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'sgo_access_token') {
        return value;
      }
    }
    return null;
  };

  const decodeJWT = (token: string) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding JWT:', error);
      return null;
    }
  };

  const fetchEntities = async () => {
    if (!organization?.id) return;

    setIsLoadingEntities(true);
    try {
      const token = getTokenFromCookies();
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/entities`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Entities from API:', data.data || []);
        setEntities(data.data || []);
      }
    } catch (error) {
      console.error('Erro ao buscar entidades:', error);
    } finally {
      setIsLoadingEntities(false);
    }
  };

  const handleOpenCreatePostModal = () => {
    setIsCreatePostModalOpen(true);
    fetchEntities();
  };

  const handleCloseCreatePostModal = () => {
    setIsCreatePostModalOpen(false);
    setCreatePostForm({
      entity_id: '',
      category_name: '',
      content: ''
    });
    setWhatsappBroadcast(false);
  };

  const handleCreatePost = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const token = getTokenFromCookies();
      if (!token || !organization?.id) {
        alert('Erro de autenticação');
        return;
      }

      // Decode token to get user_id
      const decodedToken = decodeJWT(token);
      const userId = decodedToken?.sub || decodedToken?.user_id || decodedToken?.id;

      if (!userId) {
        alert('Erro ao obter ID do usuário');
        return;
      }

      if (!createPostForm.entity_id || !createPostForm.category_name || !createPostForm.content) {
        alert('Todos os campos são obrigatórios');
        return;
      }
      
      const payload = {
        organization_id: organization.id,
        entity_id: createPostForm.entity_id,
        category_name: createPostForm.category_name,
        content: postContent,
        user_id: userId,
        whatsapp_broadcast: whatsappBroadcast
      };

      const response = await fetch(`${API_BASE_URL}/posts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        alert('Post criado com sucesso!');
        handleCloseCreatePostModal();
        await fetchPosts(); // Recarrega os posts do feed
      } else {
        const errorData = await response.text();
        alert(`Erro ao criar post: ${errorData}`);
      }
    } catch (error) {
      console.error('Erro ao criar post:', error);
      alert('Erro ao criar post');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar
        currentPath="/feed"
        onToggle={setSidebarExpanded}
      />

      <div className={`flex-1 pt-16 md:pt-8 py-8 transition-all duration-300 ml-0 ${
        sidebarExpanded ? 'md:ml-64' : 'md:ml-16'
      }`}>
        <div className="max-w-2xl mx-auto px-4">
          <div className="text-center mb-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Feed</h1>
                <p className="text-gray-600 text-sm md:text-base">Compartilhe e descubra mensagens que tocam o coração</p>
              </div>
              {isOfficialOrganization && (
                <button
                  onClick={handleOpenCreatePostModal}
                  className="flex items-center space-x-2 bg-brown-600 hover:bg-brown-700 text-white px-4 py-2 rounded-lg transition-colors text-sm md:text-base"
                >
                  <Plus className="w-5 h-5" />
                  <span>Criar Post</span>
                </button>
              )}
            </div>
          </div>

          <div className="space-y-6">
            {isLoadingPosts ? (
              <div className="text-center py-8">
                <p className="text-gray-600">Carregando posts...</p>
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-600">Nenhum post encontrado</p>
                {isOfficialOrganization && (
                  <button
                    onClick={handleOpenCreatePostModal}
                    className="mt-2 text-brown-600 hover:underline"
                  >
                    Criar primeiro post
                  </button>
                )}
              </div>
            ) : (
              posts.map((post) => (
                <PostCard
                  key={post.id}
                  postId={post.id}
                  photo={post.entity.profile_picture_url || "/santo_do_dia.png"}
                  name={post.entity.name}
                  content={post.content}
                  category={post.category_name}
                  likes={post.likes}
                  currentUserId={currentUser?.id}
                  createdAt={post.created_at}
                />
              ))
            )}

            {/* Paginação */}
            {!isLoadingPosts && posts.length > 0 && totalPages > 1 && (
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  Página {currentPage} de {totalPages} ({totalPosts} posts total)
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => fetchPosts(currentPage - 1)}
                    disabled={currentPage <= 1}
                    className="flex items-center px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Anterior
                  </button>

                  <div className="flex items-center space-x-1">
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      const page = i + 1;
                      return (
                        <button
                          key={page}
                          onClick={() => fetchPosts(page)}
                          className={`px-3 py-2 text-sm rounded-lg ${
                            page === currentPage
                              ? 'bg-brown-600 text-white'
                              : 'text-gray-600 bg-white border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    })}

                    {totalPages > 5 && (
                      <>
                        <span className="px-2 text-gray-400">...</span>
                        <button
                          onClick={() => fetchPosts(totalPages)}
                          className={`px-3 py-2 text-sm rounded-lg ${
                            totalPages === currentPage
                              ? 'bg-brown-600 text-white'
                              : 'text-gray-600 bg-white border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {totalPages}
                        </button>
                      </>
                    )}
                  </div>

                  <button
                    onClick={() => fetchPosts(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    className="flex items-center px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Próxima
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de Criação de Post */}
      {isCreatePostModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Criar Novo Post</h3>
              <button
                onClick={handleCloseCreatePostModal}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleCreatePost}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Perfil *
                </label>
                <EntitySelector
                  entities={convertEntitiesForSelector(entities)}
                  selectedEntityId={createPostForm.entity_id}
                  onSelect={(entityId) => setCreatePostForm({ ...createPostForm, entity_id: entityId })}
                  placeholder="Selecione um perfil"
                  isLoading={isLoadingEntities}
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Categoria *
                </label>
                <select
                  required
                  value={createPostForm.category_name}
                  onChange={(e) => setCreatePostForm({ ...createPostForm, category_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brown-500 focus:border-transparent"
                >
                  <option value="">Selecione uma categoria</option>
                  <option value="Palavra do Dia">Palavra do Dia</option>
                  <option value="Evangelho do Dia">Evangelho do Dia</option>
                  <option value="Santo do Dia">Santo do Dia</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Conteúdo do Post *
                </label>
                <textarea
                  required
                  value={createPostForm.content}
                  onChange={(e) =>{ 
                    setPostContent(e.target.value.replaceAll("\n", "<br>"))
                    setCreatePostForm({ ...createPostForm, content: e.target.value })
                  }}
                  placeholder="Escreva sua mensagem inspiradora..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brown-500 focus:border-transparent min-h-[120px] max-h-[300px] resize-y"
                  style={{
                    fontSize: '14px',
                    lineHeight: '1.6',
                    color: '#374151'
                  }}
                />
              </div>

              <div className="mb-6">
                <div className="flex items-center">
                  <input
                    id="whatsapp-broadcast"
                    type="checkbox"
                    checked={whatsappBroadcast}
                    onChange={(e) => setWhatsappBroadcast(e.target.checked)}
                    className="h-4 w-4 text-brown-600 focus:ring-brown-500 border-gray-300 rounded"
                  />
                  <label htmlFor="whatsapp-broadcast" className="ml-2 block text-sm text-gray-700">
                    Enviar mensagem para todos usuários do Santogram no WhatsApp
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCloseCreatePostModal}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-brown-600 hover:bg-brown-700 text-white rounded-lg transition-colors"
                >
                  Criar Post
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Feed;